import React from "react";
import { Link } from "react-router-dom";
import "./landingPage-view.css";
import Footer from "./footer";

const features = [
  {
    title: "Appointments & Calendar",
    description:
      "Book jobs, assign your team and see the whole week at a glance — drag, drop, done.",
    icon: "fas fa-calendar-check",
    tint: "tint-teal",
  },
  {
    title: "Invoices & Quotes",
    description:
      "Send branded quotes and invoices in seconds. Always know what's paid and what's overdue.",
    icon: "fas fa-file-invoice-dollar",
    tint: "tint-teal",
  },
  {
    title: "Expenses & VAT",
    description:
      "Log expenses, snap the slip, and keep your books ready for the taxman all year round.",
    icon: "fas fa-receipt",
    tint: "tint-purple",
  },
  {
    title: "Clients",
    description:
      "Every client's jobs, invoices and outstanding balance in one tidy profile.",
    icon: "fas fa-users",
    tint: "tint-indigo",
  },
  {
    title: "Vehicle Logbook",
    description:
      "Split business and private mileage and generate a SARS-ready logbook on demand.",
    icon: "fas fa-car",
    tint: "tint-amber",
  },
  {
    title: "Recurring Billing",
    description:
      "Set it once. MoneyApp invoices your retainer clients every month, automatically.",
    icon: "fas fa-sync-alt",
    tint: "tint-teal",
  },
];

const benefits = [
  {
    icon: "fas fa-bolt",
    title: "Get paid faster",
    description:
      "Send a branded invoice the moment the job's done — with a payment link. Less waiting, better cash flow.",
  },
  {
    icon: "fas fa-bell",
    title: "Never chase an invoice again",
    description:
      "See exactly who owes what, and let automatic reminders do the chasing so you don't have to.",
  },
  {
    icon: "fas fa-layer-group",
    title: "Your whole operation, one screen",
    description:
      "Jobs, team, clients and money in one place — stop juggling a diary, a spreadsheet and your accountant's software.",
  },
  {
    icon: "fas fa-clock",
    title: "Win back a day a week",
    description:
      "Quotes, invoices and logbooks that used to eat your evenings — handled in minutes, from your phone.",
  },
];

// Free-license Pexels photos (preview — swap for licensed/own photos in prod)
const PX = "?auto=compress&cs=tinysrgb&w=800";
const custom = [
  {
    icon: "fas fa-sliders-h",
    title: "Custom fields, your way",
    description:
      "Add the fields your trade needs to appointments and invoices — serial numbers, meter readings, COC numbers — and pull them straight onto the invoice.",
  },
  {
    icon: "fas fa-file-signature",
    title: "Sign-off sheets",
    description:
      "Build a sign-off sheet for any appointment. Your client signs on site and it's saved against the job, ready to attach to the invoice.",
  },
  {
    icon: "fas fa-project-diagram",
    title: "Nested clients",
    description:
      "One client, many records. A farrier adds each horse under the owner, schedules per horse, and bills the owner for all of them in one invoice.",
  },
];

// Real workflows MoneyApp businesses built on the same job-board engine.
// Click a stage to see how it looks in the app — typed fields drive the phone demo.
const workflows = [
  {
    label: "Field service",
    img: `https://images.pexels.com/photos/17018103/pexels-photo-17018103.jpeg${PX}`,
    job: "Incident · Paarl Electrical",
    desc: "A telecoms team dispatches technicians and tracks every incident from logged to invoiced.",
    stages: [
      { name: "Scheduled", fields: [
        { label: "Site address", value: "12 Main Rd, Paarl" },
        { label: "Reported fault", value: "No dial tone, line 2" },
        { label: "Technician", value: "Jarrid" } ] },
      { name: "Remote", fields: [
        { label: "Remote diagnosis", value: "Router up, port down", type: "note" },
        { label: "Resolved remotely", value: "No", type: "toggle" } ] },
      { name: "On site", fields: [
        { label: "Arrival", value: "08:42" },
        { label: "Before & after", type: "photo", subtype: "beforeafter" },
        { label: "Site sketch", type: "sketch" } ] },
      { name: "Job done", fields: [
        { label: "Work summary", value: "Replaced DB isolator, patched line", type: "note" },
        { label: "Client sign-off", type: "signature" } ] },
      { name: "Review", fields: [
        { label: "Approved by", value: "Admin" },
        { label: "QA notes", value: "OK to invoice", type: "note" } ] },
      { name: "To invoice", fields: [ { type: "invoice" } ] },
    ],
  },
  {
    label: "Property & rentals",
    img: `https://images.pexels.com/photos/8293778/pexels-photo-8293778.jpeg${PX}`,
    job: "Lease · 14 Oak Ave",
    desc: "A rental agency runs the whole lease lifecycle — FICA, signatures, deposits — through to key handover.",
    stages: [
      { name: "Application", fields: [
        { label: "Applicant", value: "M. Bekker" },
        { label: "Monthly income", value: "R 42 000" },
        { label: "Property", value: "14 Oak Ave" } ] },
      { name: "FICA received", fields: [
        { label: "ID document", type: "photo" },
        { label: "Proof of address", type: "photo" } ] },
      { name: "Lease signed", fields: [
        { label: "Start date", value: "01 Jul" },
        { label: "Deposit", value: "R 12 000" },
        { label: "Signature", type: "signature" } ] },
      { name: "Funds received", fields: [
        { label: "Deposit paid", value: "Yes", type: "toggle" },
        { label: "First month", value: "Yes", type: "toggle" } ] },
      { name: "Key handover", fields: [
        { label: "Handover date", value: "30 Jun" },
        { label: "Entry inspection", type: "photo", subtype: "beforeafter" },
        { label: "Signature", type: "signature" } ] },
    ],
  },
  {
    label: "Vet & animal care",
    img: `https://images.pexels.com/photos/677830/pexels-photo-677830.jpeg${PX}`,
    job: "Visit · Bella (horse)",
    desc: "An animal physio books an initial evaluation, schedules follow-ups, and invoices per visit.",
    stages: [
      { name: "Scheduled", fields: [
        { label: "Owner", value: "J. Pretorius" },
        { label: "Animal", value: "Bella · horse" },
        { label: "Reason", value: "Lameness check" } ] },
      { name: "Initial evaluation", fields: [
        { label: "Condition", value: "Mild left-fore lameness", type: "note" },
        { label: "Before & after", type: "photo", subtype: "beforeafter" },
        { label: "Treatment plan", value: "2-week physio", type: "note" } ] },
      { name: "Follow-up", fields: [
        { label: "Progress", value: "Full weight bearing", type: "note" },
        { label: "Next visit", value: "14 days" } ] },
      { name: "To invoice", fields: [ { type: "invoice" } ] },
    ],
  },
  {
    label: "Courier & delivery",
    img: `https://images.pexels.com/photos/4391470/pexels-photo-4391470.jpeg${PX}`,
    job: "Delivery · #DEL-1042",
    desc: "A distributor tracks each delivery from scheduled to delivered, then straight to invoicing.",
    stages: [
      { name: "Scheduled", fields: [
        { label: "Pickup", value: "Epping DC" },
        { label: "Drop-off", value: "Sea Point" },
        { label: "Parcel", value: "Medium · 4kg" } ] },
      { name: "Collected", fields: [
        { label: "Collected at", value: "09:15" },
        { label: "Parcel photo", type: "photo" } ] },
      { name: "Out for delivery", fields: [
        { label: "Driver", value: "Sipho" },
        { label: "ETA", value: "11:30" } ] },
      { name: "Delivered", fields: [
        { label: "Recipient", value: "A. Naidoo" },
        { label: "Signature", type: "signature" },
        { label: "Proof of delivery", type: "photo" } ] },
      { name: "To invoice", fields: [ { type: "invoice" } ] },
    ],
  },
];

const steps = [
  {
    n: "1",
    title: "Set up your business",
    description: "Add your company, branded templates and your team in a few minutes.",
  },
  {
    n: "2",
    title: "Work the job",
    description: "Schedule appointments, capture expenses and log work as you go.",
  },
  {
    n: "3",
    title: "Get paid",
    description: "Send the invoice, track the payment, and chase anything overdue.",
  },
];

class HomeView extends React.Component {
  state = { openTrade: 2, openFlow: 0, openStage: 2, auto: false };

  // Scroll-driven: the stage tracks how far the demo has scrolled through the
  // viewport. Any manual click hands control to the user for the rest of the session.
  demoRef = React.createRef();
  autoStopped = false;
  rafPending = false;

  componentDidMount() {
    this.setState({ auto: true });
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onScroll, { passive: true });
    this.applyScrollStage();
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onScroll);
  }

  onScroll = () => {
    if (this.rafPending) return;
    this.rafPending = true;
    window.requestAnimationFrame(this.applyScrollStage);
  };

  applyScrollStage = () => {
    this.rafPending = false;
    if (this.autoStopped) return;
    const el = this.demoRef.current;
    if (!el) return;
    const vh = window.innerHeight || 800;
    const r = el.getBoundingClientRect();
    const center = r.top + r.height / 2;
    // Map the demo's centre travelling from 82% → 18% of the viewport to stage 0 → last.
    const from = vh * 0.82;
    const to = vh * 0.18;
    let p = (from - center) / (from - to);
    p = Math.max(0, Math.min(1, p));
    const st = workflows[this.state.openFlow].stages;
    const idx = Math.round(p * (st.length - 1));
    if (idx !== this.state.openStage) this.setState({ openStage: idx });
  };

  haltAuto = () => {
    this.autoStopped = true;
    if (this.state.auto) this.setState({ auto: false });
  };

  // Renders one captured field as it appears on the job screen in the app.
  renderField(f, i) {
    if (f.type === "invoice") {
      return (
        <div className="lp-jf-invoice" key={i}>
          <div className="lp-jf-inv-head">Invoice draft</div>
          <div className="lp-jf-inv-line"><span>Labour &amp; call-out</span><span>R 850</span></div>
          <div className="lp-jf-inv-line"><span>Parts / materials</span><span>R 420</span></div>
          <div className="lp-jf-inv-total"><span>Total</span><span>R 1 270</span></div>
          <div className="lp-jf-inv-note">Everything captured on the job flowed straight onto the invoice.</div>
        </div>
      );
    }
    if (f.type === "signature") {
      return (
        <div className="lp-jf" key={i}>
          <div className="lp-jf-l">{f.label}</div>
          <div className="lp-jf-sig">
            <svg viewBox="0 0 120 34" preserveAspectRatio="none">
              <path d="M3 24 q 9 -20 16 -4 t 13 -2 q 6 12 12 0 t 14 -6 q 7 14 15 2 t 16 -4"
                fill="none" stroke="#067886" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
            <span className="lp-jf-sig-tag"><i className="fas fa-check-circle" /> Signed on site</span>
          </div>
        </div>
      );
    }
    if (f.type === "sketch") {
      return (
        <div className="lp-jf" key={i}>
          <div className="lp-jf-l">{f.label}</div>
          <div className="lp-jf-sketch">
            <svg viewBox="0 0 200 80" preserveAspectRatio="none">
              <path d="M14 60 L70 24 L120 52 L150 20 L188 46" fill="none" stroke="#DA4A4F" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="120" cy="52" r="9" fill="none" stroke="#DA4A4F" strokeWidth="3" />
            </svg>
            <span className="lp-jf-sketch-tag"><i className="fas fa-pen-nib" /> Drawn on a site photo</span>
          </div>
        </div>
      );
    }
    if (f.type === "photo") {
      const ba = f.subtype === "beforeafter";
      return (
        <div className="lp-jf" key={i}>
          <div className="lp-jf-l">{f.label}</div>
          <div className="lp-jf-photos">
            <span className="lp-jf-photo"><i className="fas fa-image" />{ba && <em>Before</em>}</span>
            <span className="lp-jf-photo"><i className="fas fa-image" />{ba && <em>After</em>}</span>
          </div>
        </div>
      );
    }
    if (f.type === "toggle") {
      return (
        <div className="lp-jf lp-jf-row" key={i}>
          <div className="lp-jf-l">{f.label}</div>
          <span className={`lp-jf-toggle ${f.value === "Yes" ? "on" : ""}`}>{f.value}</span>
        </div>
      );
    }
    // text / note
    return (
      <div className="lp-jf" key={i}>
        <div className="lp-jf-l">{f.label}</div>
        <div className={`lp-jf-v ${f.type === "note" ? "note" : ""}`}>{f.value}</div>
      </div>
    );
  }

  render() {
    const { openFlow, openStage } = this.state;
    const flow = workflows[openFlow];
    const flowStages = flow.stages;
    const sIdx = Math.min(openStage, flowStages.length - 1);
    const stage = flowStages[sIdx];
    const nextStage = flowStages[sIdx + 1];
    return (
      <section className="lp">
        {/* Drifting orbs */}
        <div className="lp-orbs" aria-hidden="true">
          <div className="lp-orb teal" />
          <div className="lp-orb coral" />
          <div className="lp-orb teal2" />
        </div>

        {/* Nav */}
        <nav className="lp-nav">
          <div className="lp-nav-inner">
            <a href="/" className="lp-logo" aria-label="MoneyApp">
              <svg className="lp-logo-mark" viewBox="0 0 131.98 75.91" aria-hidden="true">
                <circle className="lp-coin" cx="16.9" cy="59.01" r="16.9" />
                <circle className="lp-coin" cx="41.09" cy="16.9" r="16.9" />
                <circle className="lp-coin" cx="65.99" cy="59.01" r="16.9" />
                <circle className="lp-coin" cx="89.47" cy="16.9" r="16.9" />
                <circle className="lp-coin lp-coin-accent" cx="115.08" cy="59.01" r="16.9" />
              </svg>
              <span className="lp-logo-word">moneyapp</span>
            </a>
            <div className="lp-nav-actions">
              <Link to="/login" className="lp-btn lp-btn-text lp-hide-sm">
                Sign in
              </Link>
              <Link to="/register" className="lp-btn lp-btn-primary">
                Get started
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <div className="lp-wrap">
          <header className="lp-hero">
            <div className="lp-hero-text">
              <span className="lp-eyebrow">
                <i className="fas fa-bolt" /> Built for South African field workers &amp; trades
              </span>
              <h1>
                Quote, schedule, invoice — <span className="accent">get paid.</span>
              </h1>
              <p className="lp-sub">
                The all-in-one app for field workers and trades — electricians, plumbers,
                technicians and mobile teams. Book the job, quote on site, send the invoice
                and get paid — from your phone or the web.
              </p>
              <div className="lp-hero-cta">
                <Link to="/register" className="lp-btn lp-btn-primary lp-btn-lg">
                  Start free trial
                </Link>
                <a href="#pricing" className="lp-btn lp-btn-ghost lp-btn-lg">
                  See packages
                </a>
              </div>
              <ul className="lp-trust">
                <li><i className="fas fa-check-circle" /> 14-day free trial</li>
                <li><i className="fas fa-check-circle" /> No card required</li>
                <li><i className="fas fa-check-circle" /> Cancel anytime</li>
              </ul>
            </div>

            {/* Product preview card */}
            <div className="lp-preview" aria-hidden="true">
              <div className="lp-pv-hero">
                <div className="lp-pv-eyebrow">Outstanding</div>
                <div className="lp-pv-amount"><span>R</span>48 250</div>
                <div className="lp-pv-meta">6 unpaid invoices · 2 overdue</div>
              </div>

              <div className="lp-pv-row">
                <div className="lp-pv-tile tint-teal"><i className="fas fa-building" /></div>
                <div>
                  <div className="lp-pv-name">Willow Creek Stables</div>
                  <div className="lp-pv-date">INV-1042 · 12 Jun</div>
                </div>
                <div className="lp-pv-right">
                  <div className="lp-pv-val">R 12 500</div>
                  <span className="lp-pill paid">Paid</span>
                </div>
              </div>

              <div className="lp-pv-row">
                <div className="lp-pv-tile tint-coral"><i className="fas fa-bolt" /></div>
                <div>
                  <div className="lp-pv-name">Paarl Electrical CC</div>
                  <div className="lp-pv-date">INV-1039 · 28 May</div>
                </div>
                <div className="lp-pv-right">
                  <div className="lp-pv-val">R 8 900</div>
                  <span className="lp-pill overdue">14 days</span>
                </div>
              </div>

              <div className="lp-pv-row">
                <div className="lp-pv-tile tint-amber"><i className="fas fa-wrench" /></div>
                <div>
                  <div className="lp-pv-name">Garden Route Plumbing</div>
                  <div className="lp-pv-date">INV-1051 · 09 Jun</div>
                </div>
                <div className="lp-pv-right">
                  <div className="lp-pv-val">R 3 200</div>
                  <span className="lp-pill due">Due soon</span>
                </div>
              </div>
            </div>
          </header>
        </div>

        {/* Problem — the pain we solve (sets up the benefits) */}
        <div className="lp-wrap lp-band-soft">
          <section className="lp-section">
            <div className="lp-section-head">
              <div className="lp-kicker warn">Sound familiar?</div>
              <h2>You're great at the job. The admin is the problem.</h2>
              <p>Most field businesses don't lose money on the work — they lose it in the mess around it.</p>
            </div>
            <div className="lp-problems">
              <div className="lp-problem">
                <div className="lp-problem-icon"><i className="fas fa-folder-open" /></div>
                <h3>Everything's scattered</h3>
                <p>Quotes on paper, jobs in WhatsApp, a diary that double-books. Nothing talks to each other.</p>
              </div>
              <div className="lp-problem">
                <div className="lp-problem-icon"><i className="fas fa-moon" /></div>
                <h3>Admin eats your evenings</h3>
                <p>Invoices typed up at night in a spreadsheet, when you'd rather be done for the day.</p>
              </div>
              <div className="lp-problem">
                <div className="lp-problem-icon"><i className="fas fa-hand-holding-usd" /></div>
                <h3>Money slips through</h3>
                <p>Invoices you forgot to send and payments you never chased — gaps you can't even see.</p>
              </div>
            </div>
            <p className="lp-problem-bridge">
              MoneyApp pulls all of it into one place — so the admin runs itself and you actually get paid.
            </p>
          </section>
        </div>

        {/* Benefits — the WHY (outcomes) */}
        <div className="lp-benefits-band">
          <div className="lp-wrap">
            <div className="lp-section-head lp-on-dark">
              <div className="lp-kicker light">Why MoneyApp</div>
              <h2>Spend less time on admin, more on the work that pays</h2>
              <p>The point isn't more software — it's getting paid, getting time back, and always knowing where you stand.</p>
            </div>
            <div className="lp-benefits">
              {benefits.map((b) => (
                <div className="lp-benefit" key={b.title}>
                  <div className="lp-benefit-icon"><i className={b.icon} /></div>
                  <div>
                    <h3>{b.title}</h3>
                    <p>{b.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="lp-benefits-cta">
              <Link to="/register" className="lp-btn lp-btn-white lp-btn-lg">
                Start your free trial
              </Link>
            </div>
          </div>
        </div>

        {/* On the road — mobile, live updates, offline */}
        <div className="lp-wrap lp-band-surface">
          <section className="lp-section lp-road">
            <div className="lp-road-text">
              <div className="lp-kicker">Works wherever you do</div>
              <h2>Run it from the bakkie</h2>
              <p className="lp-road-lead">
                You're not at a desk all day — so MoneyApp lives on your phone too.
              </p>
              <ul className="lp-road-points">
                <li>
                  <div className="lp-road-ico tint-teal"><i className="fas fa-mobile-alt" /></div>
                  <div>
                    <h4>Mobile and web, one login</h4>
                    <p>Quote on site from the app, run the books from the office — it's the same business, everywhere.</p>
                  </div>
                </li>
                <li>
                  <div className="lp-road-ico tint-teal"><i className="fas fa-bolt" /></div>
                  <div>
                    <h4>Live updates across your team</h4>
                    <p>Assign a job and your tech sees it instantly. Statuses, messages and the job board update in real time.</p>
                  </div>
                </li>
                <li>
                  <div className="lp-road-ico tint-teal"><i className="fas fa-wifi" /></div>
                  <div>
                    <h4>Keeps working on a weak signal</h4>
                    <p>Installs to your home screen and stays loaded when the signal drops, so a dead spot on site doesn't stop the job.</p>
                  </div>
                </li>
              </ul>
              <a className="lp-appstore" href="https://apps.apple.com/us/app/moneyapp-online/id1575620079">
                <i className="fab fa-apple" /> Download on the App Store
              </a>
            </div>

            <div className="lp-phone" aria-hidden="true">
              <div className="lp-phone-frame">
                <div className="lp-phone-notch" />
                <div className="lp-phone-screen">
                  <div className="lp-phone-head">
                    <div>
                      <div className="lp-phone-eyebrow">Today · Tue 10 Jun</div>
                      <div className="lp-phone-title">Your jobs</div>
                    </div>
                    <span className="lp-phone-live"><span className="lp-dot" /> Live</span>
                  </div>
                  <div className="lp-phone-body">
                    <div className="lp-phone-row">
                      <div className="lp-pv-tile tint-teal"><i className="fas fa-bolt" /></div>
                      <div>
                        <div className="lp-pv-name">Paarl Electrical CC</div>
                        <div className="lp-pv-date">08:30 · DB board</div>
                      </div>
                      <span className="lp-pill due">Now</span>
                    </div>
                    <div className="lp-phone-row">
                      <div className="lp-pv-tile tint-amber"><i className="fas fa-wrench" /></div>
                      <div>
                        <div className="lp-pv-name">Willow Creek Stables</div>
                        <div className="lp-pv-date">11:00 · 4 horses</div>
                      </div>
                      <span className="lp-pill paid">Quoted</span>
                    </div>
                    <div className="lp-phone-row">
                      <div className="lp-pv-tile tint-indigo"><i className="fas fa-home" /></div>
                      <div>
                        <div className="lp-pv-name">G. Bekker</div>
                        <div className="lp-pv-date">14:30 · Geyser</div>
                      </div>
                      <span className="lp-pill overdue">To invoice</span>
                    </div>
                  </div>
                  <div className="lp-phone-fab"><i className="fas fa-plus" /></div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Features — the WHAT (modules) */}
        <div className="lp-wrap lp-band-soft">
          <section className="lp-section">
            <div className="lp-section-head">
              <div className="lp-kicker">Everything in one place</div>
              <h2>One app instead of five</h2>
              <p>From the first quote to the final payment — MoneyApp replaces the diary, the spreadsheet, the invoicing tool and the CRM.</p>
            </div>
            <div className="lp-features">
              {features.map((f) => (
                <div className="lp-feature" key={f.title}>
                  <div className={`lp-feature-tile ${f.tint}`}>
                    <i className={f.icon} />
                  </div>
                  <h3>{f.title}</h3>
                  <p>{f.description}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Built generic — made yours (the differentiator) */}
        <div className="lp-wrap lp-band-dark">
          <section className="lp-section">
            <div className="lp-section-head">
              <div className="lp-kicker">Built generic. Made yours.</div>
              <h2>Set it up exactly how you work</h2>
              <p>Most tools force your business into their boxes. MoneyApp is the other way around — shape the fields, sheets and records around your trade.</p>
            </div>
            <div className="lp-features lp-custom">
              {custom.map((c) => (
                <div className="lp-feature" key={c.title}>
                  <div className="lp-feature-tile tint-teal">
                    <i className={c.icon} />
                  </div>
                  <h3>{c.title}</h3>
                  <p>{c.description}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Workflow showcase — you design the pipeline (real customer setups) */}
        <div className="lp-wrap lp-band-surface">
          <section className="lp-section">
            <div className="lp-section-head">
              <div className="lp-kicker">See it in the app</div>
              <h2>You design the workflow</h2>
              <p>The job board runs on the stages <em>you</em> define, each capturing its own fields. Pick a real setup and tap through the stages to see it on the phone.</p>
            </div>
            <div className="lp-flow-tabs">
              {workflows.map((w, i) => (
                <button
                  type="button"
                  key={w.label}
                  className={`lp-flow-tab ${openFlow === i ? "active" : ""}`}
                  style={{ backgroundImage: `url(${w.img})` }}
                  onClick={() => { this.haltAuto(); this.setState({ openFlow: i, openStage: Math.min(2, w.stages.length - 1) }); }}
                >
                  <span className="lp-flow-tab-label">{w.label}</span>
                </button>
              ))}
            </div>
            <div className="lp-flow-demo" key={openFlow} ref={this.demoRef}>
              <div className="lp-flow-steps">
                <p className="lp-flow-desc">{flow.desc}</p>
                <ol className="lp-stepper">
                  {flowStages.map((s, i) => (
                    <li key={i} className={`${sIdx === i ? "active" : ""} ${i < sIdx ? "done" : ""}`}>
                      <button type="button" onClick={() => { this.haltAuto(); this.setState({ openStage: i }); }}>
                        <span className="lp-step-dot">{i < sIdx ? <i className="fas fa-check" /> : i + 1}</span>
                        <span className="lp-step-name">{s.name}</span>
                      </button>
                    </li>
                  ))}
                </ol>
                <div className="lp-flow-hint">
                  {this.state.auto
                    ? <><i className="fas fa-angle-double-down" /> Scroll to step through the stages — or tap any</>
                    : <><i className="fas fa-hand-pointer" /> Tap a stage to see the app screen</>}
                </div>
              </div>

              <div className="lp-flow-phone">
                <div className="lp-phone lp-phone-job">
                  <div className="lp-phone-frame">
                    <span className="lp-phone-notch" />
                    <div className="lp-phone-screen">
                      <div className="lp-job-head">
                        <div className="lp-job-job">{flow.job}</div>
                        <div className="lp-job-statusrow">
                          <span className="lp-job-status">{stage.name}</span>
                          <span className="lp-job-step">Stage {sIdx + 1} of {flowStages.length}</span>
                        </div>
                      </div>
                      <div className="lp-job-body" key={openFlow + "-" + sIdx}>
                        {stage.fields.map((f, i) => this.renderField(f, i))}
                      </div>
                      <button
                        type="button"
                        className="lp-job-cta"
                        onClick={() => { this.haltAuto(); this.setState({ openStage: nextStage ? sIdx + 1 : 0 }); }}
                      >
                        {nextStage
                          ? <>Move to {nextStage.name} <i className="fas fa-arrow-right" /></>
                          : <>Send invoice <i className="fas fa-paper-plane" /></>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lp-flow-more">
              <span className="lp-flow-more-label">Works for any service business —</span>
              {["Electricians", "Plumbers", "Cleaning", "Pool & garden", "Pest control", "Solar & security", "Mobile mechanics", "Beauty & wellness", "Shops & salons"].map((t) => (
                <span className="lp-trade-tag" key={t}>{t}</span>
              ))}
            </div>

            <div className="lp-mid-cta">
              <p>From a one-bakkie operation to a full team — whatever you do, MoneyApp bends to fit it.</p>
              <Link to="/register" className="lp-btn lp-btn-primary lp-btn-lg">
                Set it up for your trade
              </Link>
            </div>
          </section>
        </div>

        {/* How it works */}
        <div className="lp-wrap lp-band-soft">
          <section className="lp-section">
            <div className="lp-section-head">
              <div className="lp-kicker">How it works</div>
              <h2>Up and running today</h2>
              <p>No setup fee, no consultant, no week-long onboarding.</p>
            </div>
            <div className="lp-steps">
              {steps.map((s) => (
                <div className="lp-step" key={s.n}>
                  <div className="lp-step-num">{s.n}</div>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Packages */}
        <div className="lp-wrap lp-band-surface" id="pricing">
          <section className="lp-section">
            <div className="lp-section-head">
              <div className="lp-kicker">Packages</div>
              <h2>Pay for the seats you use</h2>
              <p>Every plan starts with a 14-day free trial. No card up front, cancel anytime.</p>
            </div>
            <div className="lp-pricing lp-pricing-3">

              <div className="lp-price-card">
                <h3>Solo</h3>
                <p className="lp-plan-sub">Just you, sorted</p>
                <div className="lp-price">
                  <span className="cur">R</span>299<span className="per"> /month</span>
                </div>
                <ul className="lp-plan-features">
                  <li><i className="fas fa-check" /> 1 user</li>
                  <li><i className="fas fa-check" /> Appointments & calendar</li>
                  <li><i className="fas fa-check" /> Invoices & quotes</li>
                  <li><i className="fas fa-check" /> Clients & expenses</li>
                  <li><i className="fas fa-check" /> Mobile + web</li>
                </ul>
                <Link to="/register" className="lp-btn lp-btn-ghost lp-btn-block">
                  Start free trial
                </Link>
              </div>

              <div className="lp-price-card featured">
                <div className="lp-price-badge">Most popular</div>
                <h3>Team</h3>
                <p className="lp-plan-sub">For a business with staff</p>
                <div className="lp-price">
                  <span className="cur">R</span>999<span className="per"> /month</span>
                </div>
                <div className="lp-price-incl">up to 5 users included</div>
                <ul className="lp-plan-features">
                  <li><i className="fas fa-check" /> Everything in Solo</li>
                  <li><i className="fas fa-check" /> Up to 5 users with roles</li>
                  <li><i className="fas fa-check" /> Recurring invoices</li>
                  <li><i className="fas fa-check" /> SARS-ready vehicle logbook</li>
                  <li><i className="fas fa-check" /> AI template setup</li>
                  <li><i className="fas fa-check" /> Priority support</li>
                </ul>
                <Link to="/register" className="lp-btn lp-btn-primary lp-btn-block">
                  Start free trial
                </Link>
              </div>

              <div className="lp-price-card">
                <h3>Business</h3>
                <p className="lp-plan-sub">Bigger teams, 6+ users</p>
                <div className="lp-price">
                  <span className="cur">+R</span>175<span className="per"> /user</span>
                </div>
                <div className="lp-price-incl">on top of Team, beyond 5 users</div>
                <ul className="lp-plan-features">
                  <li><i className="fas fa-check" /> Everything in Team</li>
                  <li><i className="fas fa-check" /> Unlimited users & roles</li>
                  <li><i className="fas fa-check" /> Multiple companies</li>
                  <li><i className="fas fa-check" /> Workflow automation</li>
                  <li><i className="fas fa-check" /> Dedicated support & onboarding</li>
                </ul>
                <Link to="/register" className="lp-btn lp-btn-ghost lp-btn-block">
                  Talk to us
                </Link>
              </div>

            </div>

            <p className="lp-price-foot">
              <i className="fas fa-users" /> Just you? <strong>R299</strong>. Growing team?
              <strong> R175 per extra user</strong> — only pay for the seats you use.
            </p>
          </section>
        </div>

        {/* Final CTA */}
        <div className="lp-cta">
          <div className="lp-cta-inner">
            <h2>Get the admin off your plate</h2>
            <p>Set up your business in minutes and send your first invoice today.</p>
            <Link to="/register" className="lp-btn lp-btn-primary lp-btn-lg">
              Start free
            </Link>
          </div>
        </div>

        <Footer />
      </section>
    );
  }
}

export default HomeView;
